<template>
  <div class="ra">
    <!-- KPIs -->
    <div class="ra-kpis">
      <analysis-stat-card
        v-for="kpi in kpis"
        :key="kpi.label"
        v-bind="kpi"
      />
    </div>

    <!-- toolbar -->
    <div class="ra-toolbar">
      <div class="ra-search">
        <feather-icon
          icon="SearchIcon"
          size="16"
        />
        <input
          v-model="search"
          type="text"
          placeholder="Search source..."
        >
      </div>

      <div class="ra-sort">
        <span class="ra-sort__label">
          <feather-icon
            icon="FilterIcon"
            size="14"
          />
          Sort by
        </span>
        <button
          v-for="option in sortOptions"
          :key="option.value"
          type="button"
          class="ra-pill"
          :class="{ 'ra-pill--active': sortBy === option.value }"
          @click="sortBy = option.value"
        >
          <feather-icon
            :icon="option.icon"
            size="13"
          />
          {{ option.label }}
        </button>
      </div>
    </div>

    <!-- source cards -->
    <div
      v-if="cards.length"
      class="ra-grid"
    >
      <div
        v-for="card in cards"
        :key="card.source"
        class="ra-card"
        :style="{ '--accent': card.theme.color, '--accent-soft': card.soft }"
        role="button"
        tabindex="0"
        @click="openDetails(card)"
        @keyup.enter="openDetails(card)"
      >
        <div class="ra-card__top">
          <div class="ra-card__icon">
            <feather-icon
              :icon="card.theme.icon"
              size="22"
            />
          </div>
          <div class="ra-card__title">
            <h5>{{ card.theme.label }}</h5>
            <span>{{ card.statusCount }} status types</span>
          </div>
          <span
            v-if="card.late > 0"
            class="ra-badge ra-badge--danger"
            title="Late requests"
          >
            <feather-icon
              icon="AlertTriangleIcon"
              size="12"
            />
            {{ format(card.late) }} late
          </span>
        </div>

        <div class="ra-card__total">
          <span class="ra-card__number">{{ format(card.total) }}</span>
          <span class="ra-card__caption">total requests</span>
        </div>

        <!-- stacked progress -->
        <div class="ra-progress">
          <div
            class="ra-progress__seg ra-progress__seg--done"
            :style="{ width: card.pct.issued + '%' }"
          />
          <div
            class="ra-progress__seg ra-progress__seg--pending"
            :style="{ width: card.pct.pending + '%' }"
          />
          <div
            class="ra-progress__seg ra-progress__seg--other"
            :style="{ width: card.pct.other + '%' }"
          />
        </div>
        <div class="ra-legend">
          <span><i class="dot dot--done" />Issued {{ card.pct.issued.toFixed(0) }}%</span>
          <span><i class="dot dot--pending" />Pending {{ card.pct.pending.toFixed(0) }}%</span>
          <span><i class="dot dot--other" />Other {{ card.pct.other.toFixed(0) }}%</span>
        </div>

        <div class="ra-mini">
          <div class="ra-mini__item">
            <feather-icon
              icon="AwardIcon"
              size="15"
              class="text-done"
            />
            <strong>{{ format(card.issued) }}</strong>
            <small>Issued</small>
          </div>
          <div class="ra-mini__item">
            <feather-icon
              icon="ClockIcon"
              size="15"
              class="text-pending"
            />
            <strong>{{ format(card.pending) }}</strong>
            <small>Pending</small>
          </div>
          <div class="ra-mini__item">
            <feather-icon
              icon="RepeatIcon"
              size="15"
              class="text-turn"
            />
            <strong>{{ format(card.inTurn) }}</strong>
            <small>In turn</small>
          </div>
        </div>

        <div class="ra-card__footer">
          View breakdown
          <feather-icon
            icon="ChevronRightIcon"
            size="16"
          />
        </div>
      </div>
    </div>

    <div
      v-else
      class="ra-empty"
    >
      <feather-icon
        icon="InboxIcon"
        size="40"
      />
      <h5>No sources found</h5>
      <p>Try a different search term.</p>
    </div>

    <source-detail-modal
      :visible.sync="modal.visible"
      :source="modal.source"
      :subtitle="modal.subtitle"
      :data="modal.data"
    />
  </div>
</template>

<script>
import AnalysisStatCard from '@/components/analysis-table/AnalysisStatCard.vue'
import SourceDetailModal from '@/components/analysis-table/SourceDetailModal.vue'
import {
  getSourceTheme, splitAnalysis, tint, toNumber, formatNumber, palette,
} from '@/components/analysis-table/analysisTheme'

export default {
  components: {
    AnalysisStatCard,
    SourceDetailModal,
  },
  props: {
    requestsAnalysisData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      search: '',
      sortBy: 'total',
      sortOptions: [
        { value: 'total', label: 'Total', icon: 'BarChart2Icon' },
        { value: 'pending', label: 'Pending', icon: 'ClockIcon' },
        { value: 'late', label: 'Late', icon: 'AlertTriangleIcon' },
      ],
      modal: {
        visible: false, source: '', subtitle: '', data: {},
      },
    }
  },
  computed: {
    sources() {
      return (this.requestsAnalysisData || []).map(item => {
        const total = toNumber(item.total_count)
        const issued = toNumber(item.certificate_issued)
        const pending = toNumber(item.total_pending)
        const other = Math.max(total - issued - pending, 0)
        const base = total || 1

        return {
          source: item.source,
          theme: getSourceTheme(item.source),
          soft: tint(getSourceTheme(item.source).color, 0.13),
          total,
          issued,
          pending,
          late: toNumber(item.late_requests),
          inTurn: toNumber(item.in_turn),
          statusCount: splitAnalysis(item).breakdown.length,
          pct: {
            issued: Math.min((issued / base) * 100, 100),
            pending: Math.min((pending / base) * 100, 100),
            other: (other / base) * 100,
          },
          raw: item,
        }
      })
    },
    cards() {
      const query = this.search.trim().toLowerCase()
      const field = { total: 'total', pending: 'pending', late: 'late' }[this.sortBy]

      return this.sources
        .filter(s => !query || s.source.toLowerCase().includes(query) || s.theme.label.toLowerCase().includes(query))
        .sort((a, b) => b[field] - a[field])
    },
    kpis() {
      const sum = key => this.sources.reduce((acc, s) => acc + s[key], 0)

      return [
        {
          label: 'Total Requests', value: sum('total'), icon: 'BarChart2Icon', color: palette.primary, hint: `${this.sources.length} sources`,
        },
        {
          label: 'Certificates Issued', value: sum('issued'), icon: 'AwardIcon', color: palette.success,
        },
        {
          label: 'Total Pending', value: sum('pending'), icon: 'ClockIcon', color: palette.warning,
        },
        {
          label: 'Late Requests', value: sum('late'), icon: 'AlertTriangleIcon', color: palette.danger,
        },
      ]
    },
  },
  methods: {
    format: formatNumber,
    openDetails(card) {
      const { source, ...data } = card.raw
      this.modal = {
        visible: true,
        source: card.source,
        subtitle: `${formatNumber(card.total)} total requests`,
        data,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$ink: #1d2b44;
$muted: #6b7a90;
$line: #d5e2f2;
$done: #13a98b;
$pending: #f0a73a;
$turn: #1c84f2;
$other: #d5dce8;

.ra {
  width: 100%;
}

.ra-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 22px;
}

/* toolbar */
.ra-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 20px;
}

.ra-search {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 260px;
  padding: 0 14px;
  height: 42px;
  color: $muted;
  background: #eef4fb;
  border: 1px solid $line;
  border-radius: 12px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: $turn;
    box-shadow: 0 0 0 3px rgba(10, 98, 232, 0.12);
  }

  input {
    flex: 1;
    border: 0;
    outline: none;
    background: transparent;
    color: $ink;
  }
}

.ra-sort {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  &__label {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-right: 4px;
    font-size: 0.82rem;
    font-weight: 600;
    color: $muted;
  }
}

.ra-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #44546a;
  background: #eef4fb;
  border: 1px solid $line;
  border-radius: 99px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: $turn;
    color: $turn;
  }

  &--active {
    color: #fff;
    background: $turn;
    border-color: $turn;

    &:hover {
      color: #fff;
    }
  }
}

/* cards */
.ra-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
  gap: 18px;
}

.ra-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 20px 14px;
  overflow: hidden;
  background: #eef4fb;
  border: 1px solid $line;
  border-radius: 18px;
  box-shadow: 0 2px 10px rgba(31, 45, 61, 0.04);
  cursor: pointer;
  outline: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s;

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 5px;
    background: var(--accent);
  }

  &:hover,
  &:focus-visible {
    transform: translateY(-4px);
    border-color: var(--accent);
    box-shadow: 0 14px 28px rgba(31, 45, 61, 0.1);

    .ra-card__footer {
      color: var(--accent);

      svg {
        transform: translateX(4px);
      }
    }
  }

  &__top {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: 13px;
    color: var(--accent);
    background: var(--accent-soft);
  }

  &__title {
    flex: 1;
    min-width: 0;

    h5 {
      margin: 0;
      font-weight: 700;
      color: $ink;
    }

    span {
      font-size: 0.76rem;
      color: $muted;
    }
  }

  &__total {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }

  &__number {
    font-size: 2.1rem;
    font-weight: 800;
    line-height: 1;
    color: $ink;
  }

  &__caption {
    font-size: 0.8rem;
    color: $muted;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    margin-top: 2px;
    padding-top: 10px;
    font-size: 0.82rem;
    font-weight: 600;
    color: $muted;
    border-top: 1px dashed $line;
    transition: color 0.2s;

    svg {
      transition: transform 0.2s;
    }
  }
}

.ra-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 9px;
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 99px;
  white-space: nowrap;

  &--danger {
    color: #c93a3f;
    background: rgba(229, 72, 77, 0.12);
  }
}

/* progress */
.ra-progress {
  display: flex;
  height: 10px;
  overflow: hidden;
  border-radius: 99px;
  background: $other;

  &__seg {
    height: 100%;
    transition: width 0.6s ease;

    &--done { background: $done; }
    &--pending { background: $pending; }
    &--other { background: $other; }
  }
}

.ra-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin-top: -6px;
  font-size: 0.72rem;
  color: $muted;

  .dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-right: 5px;
    border-radius: 50%;

    &--done { background: $done; }
    &--pending { background: $pending; }
    &--other { background: $other; }
  }
}

.ra-mini {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    padding: 9px 4px;
    background: #e3ecf8;
    border-radius: 12px;

    strong {
      font-size: 1.05rem;
      color: $ink;
    }

    small {
      font-size: 0.7rem;
      font-weight: 600;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      color: $muted;
    }
  }
}

.text-done { color: $done; }
.text-pending { color: $pending; }
.text-turn { color: $turn; }

/* empty */
.ra-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0;
  color: $muted;
}
</style>
